/**
 * Status badges. Never colour alone: every badge has a shape glyph and a word, so the
 * 11 item statuses read in greyscale and for colour-blind users.
 */
import type { ReactNode } from 'react';
import type { ApprovalState, AuditResult, AuditState, ItemStatus, PawnState, ScanResult, TransferState } from '../../lib/types';
import { TONE_SOFT, cx, type Tone } from './core';

type Glyph = 'dot' | 'ring' | 'square' | 'diamond' | 'bar' | 'cross' | 'half';

function GlyphMark({ g }: { g: Glyph }) {
  const c = 'h-2 w-2 shrink-0';
  switch (g) {
    case 'ring':
      return <span aria-hidden className={cx(c, 'rounded-full border-[1.5px] border-current')} />;
    case 'square':
      return <span aria-hidden className={cx(c, 'rounded-[1px] bg-current')} />;
    case 'diamond':
      return <span aria-hidden className={cx(c, 'rotate-45 scale-90 rounded-[1px] bg-current')} />;
    case 'bar':
      return <span aria-hidden className="h-0.5 w-2.5 shrink-0 bg-current" />;
    case 'cross':
      return <span aria-hidden className="shrink-0 text-[11px] font-bold leading-none">×</span>;
    case 'half':
      return <span aria-hidden className={cx(c, 'rounded-full border-[1.5px] border-current bg-[linear-gradient(90deg,currentColor_50%,transparent_50%)]')} />;
    default:
      return <span aria-hidden className={cx(c, 'rounded-full bg-current')} />;
  }
}

export function Badge({ tone = 'neutral', glyph = 'dot', children, className, title }: { tone?: Tone; glyph?: Glyph | null; children: ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={cx('inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold', TONE_SOFT[tone], className)}>
      {glyph && <GlyphMark g={glyph} />}
      {children}
    </span>
  );
}

type Spec = { label: string; tone: Tone; glyph: Glyph };

export const ITEM_STATUS: Record<ItemStatus, Spec> = {
  DRAFT: { label: 'Draft', tone: 'neutral', glyph: 'ring' },
  PLEDGED: { label: 'Pledged', tone: 'brass', glyph: 'square' },
  IN_STOCK: { label: 'In stock', tone: 'brand', glyph: 'dot' },
  ON_DISPLAY: { label: 'On display', tone: 'info', glyph: 'square' },
  RESERVED: { label: 'Reserved', tone: 'brass', glyph: 'diamond' },
  IN_TRANSIT: { label: 'In transit', tone: 'violet', glyph: 'half' },
  SOLD: { label: 'Sold', tone: 'neutral', glyph: 'dot' },
  UNDER_REPAIR: { label: 'Under repair', tone: 'warn', glyph: 'bar' },
  MISSING: { label: 'Missing', tone: 'danger', glyph: 'ring' },
  WRITTEN_OFF: { label: 'Written off', tone: 'danger', glyph: 'cross' },
  REDEEMED: { label: 'Redeemed', tone: 'neutral', glyph: 'bar' },
};

export const TRANSFER_STATE: Record<TransferState, Spec> = {
  REQUESTED: { label: 'Requested', tone: 'neutral', glyph: 'ring' },
  APPROVED: { label: 'Approved', tone: 'info', glyph: 'dot' },
  IN_TRANSIT: { label: 'In transit', tone: 'violet', glyph: 'half' },
  RECEIVED: { label: 'Received', tone: 'brand', glyph: 'dot' },
  DISCREPANCY: { label: 'Discrepancy', tone: 'danger', glyph: 'diamond' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral', glyph: 'cross' },
};

export const AUDIT_STATE: Record<AuditState, Spec> = {
  PLANNED: { label: 'Planned', tone: 'neutral', glyph: 'ring' },
  COUNTING: { label: 'Counting', tone: 'info', glyph: 'half' },
  COMPARED: { label: 'Compared', tone: 'info', glyph: 'square' },
  RECOUNTING: { label: 'Recounting', tone: 'warn', glyph: 'bar' },
  RECONCILING: { label: 'Reconciling', tone: 'brass', glyph: 'diamond' },
  SIGNED_OFF: { label: 'Signed off', tone: 'brand', glyph: 'ring' },
  CLOSED: { label: 'Closed', tone: 'brand', glyph: 'dot' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral', glyph: 'cross' },
};

export const AUDIT_RESULT: Record<AuditResult, Spec> = {
  MATCHED: { label: 'Matched', tone: 'brand', glyph: 'dot' },
  MISSING: { label: 'Missing', tone: 'danger', glyph: 'ring' },
  UNEXPECTED: { label: 'Unexpected', tone: 'warn', glyph: 'diamond' },
  WRONG_LOCATION: { label: 'Wrong location', tone: 'info', glyph: 'half' },
  STATUS_CONFLICT: { label: 'Status conflict', tone: 'violet', glyph: 'bar' },
};

export const SCAN_RESULT: Record<ScanResult, Spec> = {
  MATCHED: { label: 'Matched', tone: 'brand', glyph: 'dot' },
  UNKNOWN: { label: 'Unknown tag', tone: 'danger', glyph: 'ring' },
  DUPLICATE: { label: 'Already scanned', tone: 'neutral', glyph: 'bar' },
  WRONG_STATUS: { label: 'Wrong status', tone: 'violet', glyph: 'bar' },
  WRONG_BRANCH: { label: 'Other branch', tone: 'warn', glyph: 'diamond' },
  WRONG_LOCATION: { label: 'Wrong location', tone: 'info', glyph: 'half' },
  NOT_LISTED: { label: 'Not on the list', tone: 'warn', glyph: 'diamond' },
  BOUND: { label: 'Tag bound', tone: 'brand', glyph: 'dot' },
  ALREADY_BOUND: { label: 'Tag already in use', tone: 'danger', glyph: 'cross' },
  INVALID: { label: 'Not a tag', tone: 'danger', glyph: 'cross' },
};

export const PAWN_STATE: Record<PawnState, Spec> = {
  ACTIVE: { label: 'Active', tone: 'brand', glyph: 'dot' },
  RENEWED: { label: 'Renewed', tone: 'info', glyph: 'dot' },
  REDEEMED: { label: 'Redeemed', tone: 'neutral', glyph: 'ring' },
  PAST_DUE: { label: 'Past due', tone: 'warn', glyph: 'half' },
  EXPIRED: { label: 'Expired', tone: 'danger', glyph: 'diamond' },
  FORFEITED: { label: 'Forfeited', tone: 'brass', glyph: 'square' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral', glyph: 'cross' },
  LOST_TICKET: { label: 'Lost ticket', tone: 'violet', glyph: 'bar' },
};

export const APPROVAL_STATE: Record<ApprovalState, Spec> = {
  PENDING: { label: 'Waiting for approval', tone: 'brass', glyph: 'half' },
  APPROVED: { label: 'Approved', tone: 'brand', glyph: 'dot' },
  REJECTED: { label: 'Rejected', tone: 'danger', glyph: 'cross' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral', glyph: 'cross' },
};

function fromMap<K extends string>(map: Record<K, Spec>) {
  return function MappedBadge({ value, className }: { value: K; className?: string }) {
    const s = map[value] ?? { label: String(value), tone: 'neutral' as Tone, glyph: 'dot' as Glyph };
    return (
      <Badge tone={s.tone} glyph={s.glyph} className={className}>
        {s.label}
      </Badge>
    );
  };
}

export const ItemStatusBadge = fromMap(ITEM_STATUS);
export const TransferStateBadge = fromMap(TRANSFER_STATE);
export const AuditStateBadge = fromMap(AUDIT_STATE);
export const AuditResultBadge = fromMap(AUDIT_RESULT);
export const ApprovalBadge = fromMap(APPROVAL_STATE);
export const ScanResultBadge = fromMap(SCAN_RESULT);
export const PawnStateBadge = fromMap(PAWN_STATE);

/**
 * The hang tag: an item number or EPC in a notched tag with a punched hole
 * (styles.css .hang-tag). The one decorative element of EROS — use it for codes only.
 */
export function TagCode({ code, kind = 'item', className, title }: { code: string; kind?: 'item' | 'epc'; className?: string; title?: string }) {
  const short = kind === 'epc' && code.length > 16 ? `${code.slice(0, 6)}…${code.slice(-8)}` : code;
  return (
    <span
      title={title ?? (kind === 'epc' ? `EPC ${code}` : code)}
      className={cx(
        'hang-tag code inline-flex h-6 items-center whitespace-nowrap rounded-r-[4px] pr-2 text-[12.5px] font-medium',
        kind === 'item' ? 'bg-brass-soft text-ink ring-1 ring-inset ring-brass/30' : 'bg-sunken text-ink ring-1 ring-inset ring-line',
        className,
      )}
    >
      {short}
    </span>
  );
}
